// src/components/onboarding/OnboardingModal.tsx
//
// Full-screen blocking modal shown to every authenticated user whose
// hasCompletedOnboarding flag is false. Cannot be dismissed.
//
// Step 1 — Select a region     (GET /api/regions — auth required)
// Step 2 — Select a province   (GET /api/regions/:id/provinces — auth required)
//           Only shown when the selected region has active provinces.
// Step 3 — Declare interests   (local multi-select chip list — no API call)
//           Mentee picks 1+ interest areas. These drive specialist mentor matching.
//
// On submit:
//   PATCH /api/regions/complete-onboarding
//   Body: { region, province?, interests[] }
//   Then dispatch patchOnboarding({ region, province, interests, hasCompletedOnboarding: true })

import React, { useEffect, useMemo, useState } from "react";
import {
  MapPin,
  Building2,
  ChevronRight,
  Loader2,
  CheckCircle2,
  Globe,
  Heart,
  Check,
} from "lucide-react";
import {
  useGetRegionsQuery,
  useGetProvincesQuery,
  useCompleteOnboardingMutation,
  toRegionOption,
  type RegionOption,
  type ProvinceRecord,
} from "../app/api/regionApi";
import { useAppDispatch } from "../app/hooks";
import { patchOnboarding } from "../app/features/authSlice";

/* ─────────────────────────────────────────────────────────────────
   INTEREST AREAS — mirrors constants/index.js INTEREST_AREAS
   Grouped by category for the chip-select UI.
───────────────────────────────────────────────────────────────── */

interface InterestItem {
  value: string;
  label: string;
}

interface InterestCategory {
  label: string;
  colour: string; // Tailwind bg + text classes for selected chip
  items: InterestItem[];
}

const INTEREST_CATEGORIES: InterestCategory[] = [
  {
    label: "Governance & Leadership",
    colour: "bg-blue-600 text-white border-blue-600",
    items: [
      { value: "church_governance", label: "Church Governance" },
      {
        value: "organisational_leadership",
        label: "Organisational Leadership",
      },
      { value: "policy_and_advocacy", label: "Policy & Advocacy" },
      { value: "public_administration", label: "Public Administration" },
    ],
  },
  {
    label: "Business & Marketplace",
    colour: "bg-amber-600 text-white border-amber-600",
    items: [
      { value: "entrepreneurship", label: "Entrepreneurship" },
      { value: "finance_and_investment", label: "Finance & Investment" },
      { value: "marketplace_leadership", label: "Marketplace Leadership" },
      { value: "nonprofit_management", label: "Nonprofit Management" },
    ],
  },
  {
    label: "Technology & Innovation",
    colour: "bg-indigo-600 text-white border-indigo-600",
    items: [
      { value: "software_and_engineering", label: "Software & Engineering" },
      { value: "digital_ministry", label: "Digital Ministry" },
      { value: "data_and_analytics", label: "Data & Analytics" },
      { value: "product_and_design", label: "Product & Design" },
    ],
  },
  {
    label: "Creative & Media",
    colour: "bg-rose-600 text-white border-rose-600",
    items: [
      { value: "music_and_worship", label: "Music & Worship" },
      { value: "media_and_communications", label: "Media & Communications" },
      { value: "storytelling_and_writing", label: "Storytelling & Writing" },
      { value: "visual_arts", label: "Visual Arts" },
    ],
  },
  {
    label: "Community & Outreach",
    colour: "bg-green-700 text-white border-green-700",
    items: [
      { value: "social_impact", label: "Social Impact" },
      { value: "community_development", label: "Community Development" },
      { value: "youth_and_family", label: "Youth & Family" },
      { value: "missions_and_evangelism", label: "Missions & Evangelism" },
    ],
  },
  {
    label: "Personal Formation",
    colour: "bg-purple-600 text-white border-purple-600",
    items: [
      { value: "spiritual_formation", label: "Spiritual Formation" },
      { value: "leadership_character", label: "Leadership Character" },
      { value: "marriage_and_family", label: "Marriage & Family" },
    ],
  },
];



/* ─────────────────────────────────────────────────────────────────
   LOCAL PROVINCE OPTION TYPE
───────────────────────────────────────────────────────────────── */

interface ProvinceOption {
  id: string;
  name: string;
}
const toProvinceOption = (p: ProvinceRecord): ProvinceOption => ({
  id: p.id,
  name: p.name,
});

/* ─────────────────────────────────────────────────────────────────
   STEP INDICATOR
───────────────────────────────────────────────────────────────── */

type Step = 1 | 2 | 3;

const Steps: React.FC<{ step: Step; total: number }> = ({ step, total }) => (
  <div className="flex items-center gap-1.5 mb-6">
    {Array.from({ length: total }, (_, i) => {
      const n = i + 1;
      const done = n < step;
      const current = n === step;
      return (
        <React.Fragment key={n}>
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 transition-all
            ${done ? "bg-green-600 text-white" : ""}
            ${current ? "bg-green-600 text-white ring-4 ring-green-100" : ""}
            ${!done && !current ? "bg-gray-100 text-gray-400" : ""}`}
          >
            {done ? <CheckCircle2 size={13} /> : n}
          </div>
          {i < total - 1 && (
            <div
              className={`flex-1 h-0.5 rounded-full transition-all ${done ? "bg-green-600" : "bg-gray-100"}`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   REGION STEP
───────────────────────────────────────────────────────────────── */

const RegionStep: React.FC<{
  regions: RegionOption[];
  selected: RegionOption | null;
  onSelect: (r: RegionOption) => void;
  isLoading: boolean;
}> = ({ regions, selected, onSelect, isLoading }) => (
  <div>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
        <Globe size={18} className="text-green-600" />
      </div>
      <div>
        <h2 className="text-lg font-black text-gray-900">Select your region</h2>
        <p className="text-xs text-gray-500">
          Choose the RCCG Continent 2 region you belong to.
        </p>
      </div>
    </div>
    <div className="mt-5 space-y-2">
      {isLoading ? (
        Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />
        ))
      ) : regions.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">
          No regions available. Please contact your administrator.
        </p>
      ) : (
        regions.map((r) => (
          <button
            key={r.id}
            onClick={() => onSelect(r)}
            className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
              ${
                selected?.id === r.id
                  ? "border-green-600 bg-green-50"
                  : "border-gray-200 hover:border-green-300 hover:bg-green-50"
              }`}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all
              ${selected?.id === r.id ? "border-green-600 bg-green-600" : "border-gray-300"}`}
            />
            <span className="flex-1 text-sm font-semibold">{r.name}</span>
            {r.hasProvinces && (
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Provinces
              </span>
            )}
          </button>
        ))
      )}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   PROVINCE STEP
───────────────────────────────────────────────────────────────── */

const ProvinceStep: React.FC<{
  regionName: string;
  provinces: ProvinceOption[];
  selected: ProvinceOption | null;
  onSelect: (p: ProvinceOption) => void;
  isLoading: boolean;
  onBack: () => void;
}> = ({ regionName, provinces, selected, onSelect, isLoading, onBack }) => (
  <div>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
        <Building2 size={18} className="text-indigo-600" />
      </div>
      <div>
        <h2 className="text-lg font-black text-gray-900">
          Select your province
        </h2>
        <p className="text-xs text-gray-500">
          Choose your province within <strong>{regionName}</strong>
        </p>
      </div>
    </div>
    <div className="mt-5 space-y-2">
      {isLoading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />
        ))
      ) : provinces.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">
          No provinces found for this region.
        </p>
      ) : (
        provinces.map((p) => (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all
              ${
                selected?.id === p.id
                  ? "border-indigo-600 bg-indigo-50"
                  : "border-gray-200 hover:border-indigo-300 hover:bg-indigo-50"
              }`}
          >
            <div
              className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all
              ${selected?.id === p.id ? "border-indigo-600 bg-indigo-600" : "border-gray-300"}`}
            />
            <span className="text-sm font-semibold">{p.name}</span>
          </button>
        ))
      )}
    </div>
    <button
      onClick={onBack}
      className="mt-4 text-xs text-gray-400 hover:text-gray-600 font-semibold"
    >
      ← Change region
    </button>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   INTERESTS STEP
───────────────────────────────────────────────────────────────── */

const InterestsStep: React.FC<{
  selected: string[];
  onToggle: (value: string) => void;
  onSelectAll: (values: string[]) => void;
}> = ({ selected, onToggle, onSelectAll }) => {
  // Find which colour class belongs to a selected chip
  const colourFor = (value: string): string => {
    for (const cat of INTEREST_CATEGORIES) {
      if (cat.items.some((i) => i.value === value)) return cat.colour;
    }
    return "bg-gray-600 text-white border-gray-600";
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
          <Heart size={18} className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-lg font-black text-gray-900">Your interests</h2>
          <p className="text-xs text-gray-500">
            Select the areas you want to grow in. Pick as many as you like.
          </p>
        </div>
      </div>

      {/* Selected count */}
      <p className="text-[11px] font-bold text-purple-600 mb-4 uppercase tracking-widest">
        {selected.length === 0
          ? "Nothing selected yet — select at least one"
          : `${selected.length} selected`}
      </p>

      <div className="space-y-5">
        {INTEREST_CATEGORIES.map((cat) => (
          <div key={cat.label}>
            {/* Category header */}
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-black uppercase tracking-[0.16em] text-gray-400">
                {cat.label}
              </p>
              <button
                type="button"
                onClick={() => {
                  const catValues = cat.items.map((i) => i.value);
                  const allSelected = catValues.every((v) =>
                    selected.includes(v),
                  );
                  if (allSelected) {
                    // Deselect all in this category
                    onSelectAll(selected.filter((s) => !catValues.includes(s)));
                  } else {
                    // Select all in this category
                    const newSelected = Array.from(
                      new Set([...selected, ...catValues]),
                    );
                    onSelectAll(newSelected);
                  }
                }}
                className="text-[10px] font-bold text-gray-400 hover:text-gray-600 transition-colors"
              >
                {cat.items.every((i) => selected.includes(i.value))
                  ? "Deselect all"
                  : "Select all"}
              </button>
            </div>

            {/* Chips */}
            <div className="flex flex-wrap gap-2">
              {cat.items.map((item) => {
                const isSelected = selected.includes(item.value);
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => onToggle(item.value)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all
                      ${
                        isSelected
                          ? `${colourFor(item.value)} shadow-sm`
                          : "border-gray-200 text-gray-600 hover:border-gray-400 bg-white"
                      }`}
                  >
                    {isSelected && <Check size={11} className="shrink-0" />}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-5 text-[11px] text-gray-400 leading-relaxed">
        Your interests help us match you with the right specialist mentors. You
        can update them anytime from your profile settings.
      </p>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const OnboardingModal: React.FC = () => {
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Step>(1);
  const [selectedRegion, setSelectedRegion] = useState<RegionOption | null>(
    null,
  );
  const [selectedProvince, setSelectedProvince] =
    useState<ProvinceOption | null>(null);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  /* ── Step 1: Regions ─────────────────────────────────────────── */

  const { data: regionsRes, isLoading: regionsLoading } = useGetRegionsQuery({
    archived: false,
  });

  const regions: RegionOption[] = useMemo(
    () => (regionsRes?.data ?? []).map(toRegionOption),
    [regionsRes],
  );

  /* ── Step 2: Provinces ───────────────────────────────────────── */

  const { data: provincesRes, isFetching: provincesLoading } =
    useGetProvincesQuery(
      { regionId: selectedRegion?.id ?? "", archived: false },
      { skip: !selectedRegion?.id || !selectedRegion.hasProvinces },
    );

  const provinces: ProvinceOption[] = useMemo(
    () =>
      (provincesRes?.data ?? [])
        .filter((p) => !p.isArchived)
        .map(toProvinceOption),
    [provincesRes],
  );

  /* ── Mutation ────────────────────────────────────────────────── */

  const [completeOnboarding, { isLoading: submitting }] =
    useCompleteOnboardingMutation();

  /* ── Auto-advance region → province step ─────────────────────── */
  useEffect(() => {
    if (!selectedRegion) return;
    if (selectedRegion.hasProvinces) {
      setStep(2);
      setSelectedProvince(null);
    } else {
      setStep(1);
      setSelectedProvince(null);
    }
  }, [selectedRegion]);

  const hasProvinceOptions =
    selectedRegion?.hasProvinces && provinces.length > 0;

  // How many steps are there in total for this user's path?
  // Province step is skipped if region has no provinces.
  const totalSteps: number = hasProvinceOptions ? 3 : 2;

  // Visual step number (province is step 2, interests is always last)
  const visualStep: Step = step === 3 ? 3 : step === 2 ? 2 : 1;

  /* ── Submission state ────────────────────────────────────────── */

  const canAdvanceFromRegion = selectedRegion !== null;

  const canAdvanceFromProvince =
    selectedProvince !== null || provinces.length === 0;

  const canSubmit = selectedInterests.length > 0;

  const handleToggleInterest = (value: string) => {
    setSelectedInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSetAllInterests = (values: string[]) => {
    setSelectedInterests(values);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!selectedRegion) {
        setError("Please select your region to continue.");
        return;
      }
      setError(null);
      if (hasProvinceOptions) {
        setStep(2);
      } else {
        setStep(3); // Skip province step
      }
    } else if (step === 2) {
      setError(null);
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRegion) return;
    if (selectedInterests.length === 0) {
      setError("Please select at least one interest area to continue.");
      return;
    }
    setError(null);
    try {
      await completeOnboarding({
        region: selectedRegion.id,
        province: selectedProvince?.id ?? undefined,
        interests: selectedInterests,
      }).unwrap();

      dispatch(
        patchOnboarding({
          region: selectedRegion.id,
          province: selectedProvince?.id ?? null,
          interests: selectedInterests,
          hasCompletedOnboarding: true,
        }),
      );
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  /* ── Render ──────────────────────────────────────────────────── */

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md h-[99vh] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-green-700 to-green-950 px-7 pt-7 pb-6 text-white shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={14} className="text-green-300" />
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-green-300">
              Programme setup
            </span>
          </div>
          <h1 className="text-2xl font-black leading-tight mb-1">
            Welcome to GIMLP
          </h1>
          <p className="text-green-200 text-sm leading-relaxed">
            {step === 1 &&
              "Tell us which region you're part of to connect you with the right community."}
            {step === 2 && "Select your province within the region."}
            {step === 3 &&
              "Tell us what you'd like to grow in — this helps us match you with the right mentors."}
          </p>
        </div>

        {/* Body */}
        <div className="px-7 py-6 flex flex-col flex-1 overflow-hidden">
          <Steps step={visualStep} total={totalSteps} />

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto pr-1">
            {step === 1 && (
              <RegionStep
                regions={regions}
                selected={selectedRegion}
                onSelect={(r) => {
                  setSelectedRegion(r);
                  setError(null);
                }}
                isLoading={regionsLoading}
              />
            )}
            {step === 2 && selectedRegion && (
              <ProvinceStep
                regionName={selectedRegion.name}
                provinces={provinces}
                selected={selectedProvince}
                onSelect={(p) => {
                  setSelectedProvince(p);
                  setError(null);
                }}
                isLoading={provincesLoading}
                onBack={() => {
                  setStep(1);
                  setSelectedProvince(null);
                  setSelectedRegion(null);
                }}
              />
            )}
            {step === 3 && (
              <InterestsStep
                selected={selectedInterests}
                onToggle={handleToggleInterest}
                onSelectAll={handleSetAllInterests}
              />
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 shrink-0">
              {error}
            </p>
          )}

          {/* CTA */}
          <div className="mt-4 pt-4 border-t border-gray-100 shrink-0">
            <button
              onClick={step === 3 ? handleSubmit : handleNext}
              disabled={
                (step === 1 && !canAdvanceFromRegion) ||
                (step === 2 && !canAdvanceFromProvince) ||
                (step === 3 && (!canSubmit || submitting))
              }
              className="w-full rounded-xl bg-green-600 hover:bg-green-500 active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-sm px-6 py-3.5 flex items-center justify-center gap-2 transition-all"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Saving…
                </>
              ) : step === 3 ? (
                <>
                  <CheckCircle2 size={16} /> Confirm &amp; enter programme
                </>
              ) : (
                <>
                  Next <ChevronRight size={16} />
                </>
              )}
            </button>

            {/* Skip interests (allowed — they can set later from profile) */}
            {step === 3 && !submitting && (
              <button
                type="button"
                onClick={async () => {
                  setError(null);
                  try {
                    await completeOnboarding({
                      region: selectedRegion!.id,
                      province: selectedProvince?.id ?? undefined,
                      interests: [],
                    }).unwrap();
                    dispatch(
                      patchOnboarding({
                        region: selectedRegion!.id,
                        province: selectedProvince?.id ?? null,
                        interests: [],
                        hasCompletedOnboarding: true,
                      }),
                    );
                  } catch {
                    setError("Something went wrong. Please try again.");
                  }
                }}
                className="w-full mt-2 text-center text-xs text-gray-400 hover:text-gray-600 font-semibold py-1.5 transition-colors"
              >
                Skip for now — I'll set my interests from my profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
